<script lang="ts">
  import { of, BehaviorSubject, debounceTime, switchMap, catchError, startWith, map, combineLatest, Subject } from "rxjs";
  import { fromFetch } from "rxjs/fetch";
  import type { ReverseGeoLocationResponse, SearchAddressResult, TypeaheadLocationResponse } from "../../../typings/Location";
  import { request } from "../../api/ApiHelpers";
  import { v4 as uuidv4 } from "uuid";
  import { userStore } from "../stores/UserStore";
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { get } from "svelte/store";
  import { KeysEnum } from "../../models/KeysEnum";

  // component props
  export let placeholder: string = "";
  export let apiGetSearchAddress: string = "";
  export let apiGetSearchAddressReverse: string = "";
  export let isInputClear: boolean = true;
  export let selectedSearchAddressResult: SearchAddressResult = null;
  export let labelFloatInside: boolean = true;
  export let invalidAddressMessage: string = "";
  export let dropdownOptions: string[] = [];
  export let selectedDropdownOption = "";
  export let onSubmitWithDropdown: () => void;
  export let onSelectDropdownOption: (option: string) => void;

  export const showErrorMessageExternal = () => (showErrorMessage = true);
  export let onClear: () => void;

  const dispatch = createEventDispatcher();
  const { userAddress, userGeoCoords } = userStore;
  const componentId = uuidv4();
  let latestSearchAddressResults: SearchAddressResult[] = [];

  // set typeahead input value as an rxjs BehaviorSubject
  const downSubject = new Subject<number>();
  const typeAhead = new BehaviorSubject("");
  let latestInputVal: string = "";
  typeAhead["set"] = typeAhead.next;
  typeAhead.subscribe((x) => {
    isInputClear = x.length === 0;
    latestInputVal = x;
    downSubject.next(-1);
  });

  // input focus switches (for suggestions)
  let isFocused = false;
  const onFocus = () => (isFocused = true);
  const onBlur = (e: FocusEvent) => {
    const clickedEl = e.relatedTarget as HTMLElement;
    if (clickedEl == null || !clickedEl.classList.contains("location-typeahead__item")) {
      // if 1 result, set that result before unfocusing
      if (latestSearchAddressResults && latestSearchAddressResults.length) {
        userAddress.set(latestSearchAddressResults[0]);
      }

      isFocused = false;
    } // do not lose focus when the selected element is a suggestion
  };

  let isDropdownOpen = false;

  function toggleDropdown() {
    isDropdownOpen = !isDropdownOpen;
  }

  function handleSelectDropdown (option) {
    if (onSelectDropdownOption) {
      onSelectDropdownOption(option);
    }
    isDropdownOpen = false;
  }

  onMount(() => {
    function handleClickOutside(event) {
      if (isDropdownOpen && !event.target.closest(".location-typeahead__address-type-dropdown")) {
        isDropdownOpen = false;
      }
    }

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  });

  $: hasSuggestionsOpen = isFocused && $typeaheadResponse.results !== null && $typeaheadResponse.results.length > 0;

  // error message logic
  $: showErrorMessage =
    (latestInputVal.length > 0 && isFocused && $modifiedResults.results && $modifiedResults.results.length === 0) ||
    (latestInputVal.length > 0 && !isFocused && $userAddress?.address.freeformAddress !== latestInputVal);
  $: if (showErrorMessage !== null) {
    dispatch("validChange", {
      isValid: !showErrorMessage,
    });
  }

  // results are subscribed to typeAhead input value
  const typeaheadResponse = typeAhead.pipe(
    debounceTime(350),
    switchMap((query) => {
      if (!query) return of({ results: null } as TypeaheadLocationResponse);

      // build get request w/ optional lat/lon coordinates from store
      let typeAheadSearchUrl = `${apiGetSearchAddress}&query=${query}&typeahead=true`;
      const updatedCoords = get(userGeoCoords);
      if (updatedCoords != null && updatedCoords.hasOwnProperty("latitude")) {
        typeAheadSearchUrl = `${typeAheadSearchUrl}&lat=${updatedCoords.latitude}&lon=${updatedCoords.longitude}`;
      }

      return fromFetch(typeAheadSearchUrl).pipe(
        switchMap((response) => {
          if (response.ok) {
            return response.json() as Promise<TypeaheadLocationResponse>;
          }
          return of<TypeaheadLocationResponse>({
            error: true,
            message: `Error ${response.status}`,
            errorMessage: null,
            results: null,
            status: 500,
          });
        }),
        catchError((err) => {
          if (process.env.dev) {
            console.log(JSON.stringify(err));
          }

          return of<TypeaheadLocationResponse>({
            error: true,
            message: err.message,
            errorMessage: err.message,
            results: null,
            status: 500,
          });
        })
      );
    }),
    startWith({ results: null } as TypeaheadLocationResponse)
  );
  typeaheadResponse.subscribe((response) => {
    latestSearchAddressResults = response.results;
  });

  const modifiedResults = combineLatest([typeaheadResponse, downSubject]).pipe(
    map(([typeaheadResponse, downValue]) => {
      if (!typeaheadResponse.results) return typeaheadResponse;

      if (downValue === KeysEnum.enter) {
        const selectedSuggestion = typeaheadResponse.results.find((item) => item.isHighlighted) || typeaheadResponse.results[0];
        handleSuggestionClick(selectedSuggestion);
      } else if (typeaheadResponse.results.length > 1 && downValue !== -1) {
        // do highlighting based on key inputs
        const currentIndex = typeaheadResponse.results.findIndex((item) => item.isHighlighted);
        let nextIndex = currentIndex === -1 || currentIndex > typeaheadResponse.results.length - 1 ? 0 : currentIndex + 1; // default to down
        if (downValue === KeysEnum.up && currentIndex !== 0) {
          nextIndex = currentIndex - 1;
        }

        if (currentIndex !== -1) {
          typeaheadResponse.results[currentIndex].isHighlighted = false;
        }
        typeaheadResponse.results[nextIndex].isHighlighted = true;
      }
      return typeaheadResponse;
    }),
    startWith({ results: null } as TypeaheadLocationResponse)
  );

  function handleGeoLocationClick() {
    function success(position) {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      // update user store geolocation
      userGeoCoords.set({ latitude: latitude, longitude: longitude });

      // reverse geo-code lat-lng into address
      request<ReverseGeoLocationResponse>({
        url: apiGetSearchAddressReverse,
        params: { query: `${latitude},${longitude}` },
      })
        .then((response) => {
          if (response.addresses.length) {
            userAddress.set(response.addresses[0]);
          }
        })
        .catch((e) => {
          console.log(e);
        });
    }

    function error() {
      console.log("Unable to retrieve the users location");
    }
    navigator.geolocation.getCurrentPosition(success, error);
  }

  // place user selection in user store for other component usage
  function handleSuggestionClick(result: SearchAddressResult) {
    if (process.env.dev) {
      console.log(result);
    }

    selectedSearchAddressResult = result;
    userAddress.set(result);

    // update user store geolocation
    userGeoCoords.set({ latitude: result.position.lat, longitude: result.position.lon, });

    isFocused = false;
  }

  function onInputKeyUp(e) {
    switch (e.keyCode) {
      case KeysEnum.up:
      case KeysEnum.down:
      case KeysEnum.enter:
        if (hasSuggestionsOpen) {
          downSubject.next(e.keyCode);
        }
        break;
    }


    if (onSubmitWithDropdown) {
      onSubmitWithDropdown();
    }
  }

  // whenever the address from user store is updated, update the typeAhead input with the display address
  const unsubscribe = userAddress.subscribe((result) => {
    if (!result) {
      return;
    }
    selectedSearchAddressResult = result;
    typeAhead.next(result.address.freeformAddress);
  });

  onDestroy(unsubscribe);

  function handleClear() {
    if (onClear) {
      onClear();
    }
    typeAhead.next("")
  }
</script>

<div class="location-typeahead">
  <div
    class="input-group input-group--float-label"
    class:empty={$typeAhead.length === 0}
    class:input-group--float-label-inside={labelFloatInside}
    class:non-empty-dropdown={dropdownOptions.length !== 0 && selectedDropdownOption != ""}
    class:validated={!showErrorMessage && !isFocused}
  >
    <button class="btn btn-outline-secondary btn-normal btn-location" type="button" on:click={handleGeoLocationClick}>
      <svg viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
        <path d="m22 0-9.778 22V9.778H0z" fill="#C10016" fill-rule="evenodd" />
      </svg>
    </button>
    {#if dropdownOptions.length === 0}
      <input
        type="text"
        name="locationTypeahead{componentId}"
        class="location-typeahead__input form-control form-control-lg"
        role="combobox"
        aria-owns="location-typeahead__list--{componentId}"
        aria-autocomplete="list"
        bind:value={$typeAhead}
        autocomplete="off"
        on:blur={onBlur}
        on:focus={onFocus}
        on:keyup={onInputKeyUp}
        {placeholder}
      />
    {:else}
      <input
        type="text"
        name="locationTypeahead{componentId}"
        class="location-typeahead__input form-control form-control-lg"
        role="combobox"
        aria-owns="location-typeahead__list--{componentId}"
        aria-autocomplete="list"
        bind:value={$typeAhead}
        autocomplete="off"
        on:focus={onFocus}
        {placeholder}
      />
    {/if}
    <button type="button" class="btn btn-normal btn-clear"
            style={dropdownOptions.length == 0 ? "right: 0;": ""}
            on:click={handleClear}>
      <i class="bi bi-x-lg" />
    </button>
    {#if dropdownOptions.length !== 0}
      <div class="location-typeahead__address-type-dropdown" class:open={isDropdownOpen}>
        <div class="dropdown-selected" on:click={toggleDropdown}>
          <div>
            {#if selectedDropdownOption}
              {selectedDropdownOption}
            {:else}
              Select Address Type
            {/if}
          </div>
          <img src="/images/portal-icons/atom-icon-ui-dropdown-down.svg" alt="dropdown icon" />
        </div>
        <ul class="dropdown-options">
          {#each dropdownOptions as option}
            <li on:click={() => handleSelectDropdown(option)} class="dropdown-option">{option}</li>
          {/each}
        </ul>
      </div>
      <button
        type="button"
        disabled={selectedDropdownOption == "" || $typeAhead.length == 0}
        class="btn btn-primary btn-location-submit btn-state-table-schedule"
        class:disabled={selectedDropdownOption == "" || $typeAhead.length == 0}
        on:click={onInputKeyUp}
      >
        Show Schedule
      </button>
    {/if}
  </div>
  <div class="location-typeahead__suggests-wrapper">
    <ul
      id="location-typeahead__list--{componentId}"
      class="location-typeahead__suggests"
      class:location-typeahead__suggests--hidden={!hasSuggestionsOpen}
      role="listbox"
    >
      {#if $modifiedResults.results}
        {#each $modifiedResults.results as result (result.id)}
          <li
            class="location-typeahead__item"
            id={result.id}
            role="option"
            tabindex="-1"
            aria-selected={result.isHighlighted ? "true" : "false"}
            on:click={() => {
              handleSuggestionClick(result);
            }}
          >
            <span>{result.address.freeformAddress}</span>
          </li>
        {/each}
      {/if}
    </ul>
    <div aria-live="polite" role="status" class="visually-hidden">
      {$typeaheadResponse.results && $typeaheadResponse.results.length ? $typeaheadResponse.results.length : 0} results available.
    </div>
  </div>
  {#if showErrorMessage}
    <div class="location-typeahead__error-message">
      <i class="bi bi-exclamation text-primary lh-1" />
      <p class="text-primary mb-0 location-typeahead__error-message-desc">
        {@html invalidAddressMessage}
      </p>
    </div>
  {/if}
  <!-- <pre>{ JSON.stringify($results) }</pre> -->
</div>
